
import React, { useState, useEffect, useRef } from 'react';
import { p2pService } from './services/p2pService';
import { ConnectionState, ChatMessage, FileTransferProgress } from './types';
import { Button } from './components/Button';
import { 
  LucideSend, LucidePlus, LucideMoon, LucideSun, 
  LucideDownload, LucideFile, LucideLogOut, 
  LucideCheckCircle2, LucideAlertCircle,
  LucideUser, LucideUsers
} from 'lucide-react';

// Toast Type
interface Toast {
  id: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

const App = () => {
  // --- State ---
  const [theme, setTheme] = useState<'light' | 'dark'>(() => 
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
  
  const [step, setStep] = useState<'landing' | 'chat'>('landing');
  const [status, setStatus] = useState<ConnectionState>(ConnectionState.IDLE);
  const [error, setError] = useState<string | null>(null);

  // Form
  const [name, setName] = useState('');
  const [partnerInput, setPartnerInput] = useState('');
  
  // Chat
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMsg, setInputMsg] = useState('');
  const [partnerName, setPartnerName] = useState('Partner');
  
  // File Transfer
  const [fileProgress, setFileProgress] = useState<FileTransferProgress | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);

  // --- Effects ---

  useEffect(() => {
    // Theme toggle logic
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  }, [theme]);

  useEffect(() => {
    // Scroll to bottom on new message
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, fileProgress]);

  // P2P Event Listeners - CRITICAL: Dependencies MUST include partnerName to update state correctly
  // BUT we must cleanup listeners without disconnecting the session.
  useEffect(() => {
    
    const handleStatus = (s: any) => {
      console.log('App Status Update:', s);
      if (s === 'waiting') {
         setStatus(ConnectionState.CONNECTING);
         addToast("You are the Host. Waiting for partner...", "info");
      }
      if (s === 'connecting') {
        setStatus(ConnectionState.CONNECTING);
        addToast("Connecting to partner...", "info");
      }
      if (s === 'connected') {
        setStatus(ConnectionState.CONNECTED);
        addToast("Secure P2P Connection Established", "success");
      }
      if (s === 'disconnected') {
         setStatus(ConnectionState.DISCONNECTED);
      }
      if (s === 'error') {
         setStatus(ConnectionState.ERROR);
      }
    };

    const handleHandshake = (data: any) => {
      setPartnerName(data.name || 'Partner');
      addToast(`${data.name || 'Partner'} connected`, "success");
      setStatus(ConnectionState.CONNECTED);
    };

    const handleMessage = (msg: ChatMessage) => {
      setMessages(prev => [...prev, { ...msg, senderName: partnerName }]);
    };

    const handleMessageSent = (msg: ChatMessage) => {
      setMessages(prev => [...prev, msg]);
    };

    const handleFileProgress = (prog: FileTransferProgress) => {
      setFileProgress(prog.progress === 100 ? null : prog);
    };

    const handlePartnerDisconnect = () => {
      setStatus(ConnectionState.DISCONNECTED);
      addToast("Partner disconnected", "error");
    };

    const handleError = (err: string) => {
      setError(err);
      addToast(err, "error");
    };
    
    const handleInfo = (msg: string) => {
        addToast(msg, "info");
    };

    // Bind
    p2pService.on('status', handleStatus);
    p2pService.on('handshake-received', handleHandshake);
    p2pService.on('message', handleMessage);
    p2pService.on('message-sent', handleMessageSent);
    p2pService.on('file-progress', handleFileProgress);
    p2pService.on('partner-disconnected', handlePartnerDisconnect);
    p2pService.on('error', handleError);
    p2pService.on('info', handleInfo);

    // Cleanup: Unsubscribe ONLY. DO NOT DISCONNECT.
    return () => {
      p2pService.off('status', handleStatus);
      p2pService.off('handshake-received', handleHandshake);
      p2pService.off('message', handleMessage);
      p2pService.off('message-sent', handleMessageSent);
      p2pService.off('file-progress', handleFileProgress);
      p2pService.off('partner-disconnected', handlePartnerDisconnect);
      p2pService.off('error', handleError);
      p2pService.off('info', handleInfo);
    };
  }, [partnerName]); // Re-bind if partnerName changes to update closures

  // --- Helpers ---

  const addToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const generateRoomId = (n1: string, n2: string) => {
    // deterministic room ID: alphabetically sorted, lowercase, stripped of spaces
    const clean1 = n1.trim().toLowerCase().replace(/\s+/g, '');
    const clean2 = n2.trim().toLowerCase().replace(/\s+/g, '');
    const sorted = [clean1, clean2].sort();
    return `${sorted[0]}-${sorted[1]}`;
  };

  // --- Handlers ---

  const handleConnect = () => {
    if (!name || !partnerInput) return;

    setError(null);
    setStep('chat');
    setStatus(ConnectionState.CONNECTING);
    setPartnerName(partnerInput);
    
    // Generate the unique room ID
    const roomId = generateRoomId(name, partnerInput);
    console.log("Connecting to room:", roomId);

    p2pService.initSession(roomId, name);
  };

  const handleSendText = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputMsg.trim()) return;
    
    const msg = p2pService.sendText(inputMsg, 'me', name);
    setMessages(prev => [...prev, msg]);
    setInputMsg('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await p2pService.sendFile(file, 'me', name);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetSession = (fullReset = true) => {
    if (fullReset) {
      p2pService.disconnect();
      setStep('landing');
      setMessages([]);
      setStatus(ConnectionState.IDLE);
      setFileProgress(null);
      setName('');
      setPartnerInput('');
    }
  };

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  // --- Landing Screen ---

  if (step === 'landing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
        {/* Abstract Background Shapes */}
        <div className="absolute top-[-20%] right-[-10%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-[-20%] left-[-10%] w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Toast Container */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4 flex flex-col gap-2">
          {toasts.map(t => (
            <div key={t.id} className={`p-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 animate-in slide-in-from-top-2 fade-in ${
              t.type === 'error' ? 'bg-red-500 text-white' : t.type === 'success' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
            }`}>
              {t.message}
            </div>
          ))}
        </div>

        <div className="absolute top-6 right-6 z-20 flex gap-2">
          <button onClick={toggleTheme} className="p-2 rounded-full bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 backdrop-blur-sm transition-all shadow-sm">
            {theme === 'light' ? <LucideMoon size={20} /> : <LucideSun size={20} />}
          </button>
        </div>

        <div className="w-full max-w-md bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700 overflow-hidden z-10 flex flex-col">
          
          <div className="p-8 text-center pb-6">
            <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">GhostLink</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Secure Peer-to-Peer Connection</p>
          </div>

          <div className="p-8 pt-0 space-y-6">
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400 flex items-center gap-2">
                   <LucideUser size={14} /> Your Name
                </label>
                <input 
                  value={name} onChange={e => setName(e.target.value)} 
                  className="w-full p-3 rounded-lg bg-gray-100 dark:bg-gray-900 border border-transparent focus:border-blue-500 focus:bg-white dark:focus:bg-black transition-all outline-none font-medium"
                  placeholder="Enter your name"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400 flex items-center gap-2">
                   <LucideUsers size={14} /> Partner's Name
                </label>
                <input 
                  value={partnerInput} onChange={e => setPartnerInput(e.target.value)} 
                  className="w-full p-3 rounded-lg bg-gray-100 dark:bg-gray-900 border border-transparent focus:border-purple-500 focus:bg-white dark:focus:bg-black transition-all outline-none font-medium"
                  placeholder="Enter partner's name"
                />
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg text-xs leading-relaxed">
                 ℹ️ <strong>How it works:</strong> Both you and your partner must enter the <strong>exact same names</strong>. The order doesn't matter (e.g. "Alice" + "Bob" connects to "Bob" + "Alice").
              </div>

              <Button onClick={handleConnect} disabled={!name || !partnerInput} className="w-full font-bold text-lg shadow-lg shadow-blue-500/20 mt-2">
                Connect
              </Button>
            </div>
          </div>

          {error && <div className="mx-8 mb-6 p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg text-center font-medium animate-in shake">{error}</div>}
        </div>
      </div>
    );
  }

  // --- Chat Screen ---

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 relative">
      {/* Toasts */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4 flex flex-col gap-2">
          {toasts.map(t => (
            <div key={t.id} className={`p-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 animate-in slide-in-from-top-2 fade-in ${
              t.type === 'error' ? 'bg-red-500 text-white' : t.type === 'success' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
            }`}>
              {t.type === 'success' && <LucideCheckCircle2 size={16} />}
              {t.type === 'error' && <LucideAlertCircle size={16} />}
              {t.message}
            </div>
          ))}
      </div>

      {/* Header */}
      <header className="flex-none px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shadow-sm z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
             {partnerName.charAt(0).toUpperCase()}
          </div>
          <div className="flex flex-col">
            <h2 className="font-bold text-lg leading-tight flex items-center gap-2">
              {partnerName}
            </h2>
            <div className="flex items-center gap-1">
               <span className={`w-2 h-2 rounded-full ${status === ConnectionState.CONNECTED ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`}></span>
               <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{status.toLowerCase()}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-400">
            {theme === 'light' ? <LucideMoon size={18} /> : <LucideSun size={18} />}
          </button>
          
          <button onClick={() => resetSession(true)} className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors" title="Disconnect">
            <LucideLogOut size={18} />
          </button>
        </div>
      </header>

      {/* Messages Area */}
      <main className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth">
        {status === ConnectionState.CONNECTING && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 animate-pulse">
            <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-gray-800 mb-6 flex items-center justify-center relative">
              <span className="text-4xl animate-bounce">📡</span>
              <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping"></div>
            </div>
            <h3 className="text-xl font-bold text-gray-600 dark:text-gray-300 mb-2">
              Searching for {partnerName}...
            </h3>
            <p className="text-sm max-w-xs text-center">Tell {partnerName} to open the app and enter your name.</p>
          </div>
        )}

        {status === ConnectionState.DISCONNECTED && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
              <LucideLogOut size={32} />
            </div>
            <p className="font-medium">Partner disconnected</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-blue-500 hover:underline">Start New Chat</button>
          </div>
        )}

        {status === ConnectionState.CONNECTED && messages.length === 0 && (
           <div className="text-center py-8 opacity-60">
             <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm font-medium">
               <LucideCheckCircle2 size={16} />
               Secure Direct Connection
             </div>
             <p className="mt-4 text-sm text-gray-500">Say hello to {partnerName}!</p>
           </div>
        )}

        {messages.map((msg) => {
          const isMe = msg.senderId === 'me';
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl p-3 shadow-sm relative group ${
                isMe 
                  ? 'bg-blue-600 text-white rounded-br-none' 
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none border border-gray-100 dark:border-gray-700'
              }`}>
                {/* Text Content */}
                {msg.text && <p className="break-words whitespace-pre-wrap leading-relaxed">{msg.text}</p>}
                
                {/* Image Content */}
                {msg.type === 'image' && msg.fileUrl && (
                  <div className="mt-1 rounded-lg overflow-hidden border border-white/20 relative">
                    <img src={msg.fileUrl} alt="Received" className="max-w-full h-auto max-h-64 object-contain" />
                    <a href={msg.fileUrl} download={msg.fileName} className="absolute bottom-2 right-2 bg-black/50 p-2 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm hover:bg-black/70">
                       <LucideDownload size={16} />
                    </a>
                  </div>
                )}

                {/* File Content */}
                {msg.type === 'file' && (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${isMe ? 'bg-blue-700/50' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <div className={`p-2 rounded-lg ${isMe ? 'bg-white/20' : 'bg-white dark:bg-gray-600'}`}>
                      <LucideFile size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{msg.fileName}</p>
                      <p className="text-xs opacity-70">{(msg.fileSize! / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    {msg.fileUrl && (
                      <a href={msg.fileUrl} download={msg.fileName} className={`p-2 rounded-full transition-colors ${isMe ? 'hover:bg-white/20' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}>
                        <LucideDownload size={20} />
                      </a>
                    )}
                  </div>
                )}
                
                <span className={`text-[10px] block text-right mt-1 ${isMe ? 'text-blue-100/70' : 'text-gray-400'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        
        {/* Progress Bar */}
        {fileProgress && (
          <div className="flex justify-center my-4 sticky bottom-0 z-10">
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur shadow-xl rounded-full pl-2 pr-5 py-2 flex items-center gap-3 border border-gray-200 dark:border-gray-700 animate-in slide-in-from-bottom-5 w-full max-w-sm">
              <div className={`p-2 rounded-full shrink-0 ${fileProgress.direction === 'outgoing' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                {fileProgress.direction === 'outgoing' ? <LucideSend size={16} /> : <LucideDownload size={16} />}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex justify-between items-end mb-1">
                  <span className="text-xs font-bold truncate pr-2">{fileProgress.fileName}</span>
                  <span className="text-xs font-mono text-gray-500">{fileProgress.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${fileProgress.direction === 'outgoing' ? 'bg-blue-500' : 'bg-green-500'}`}
                    style={{ width: `${fileProgress.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </main>

      {/* Input Area */}
      <footer className="flex-none bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-3 sm:p-4 z-20">
        <form onSubmit={handleSendText} className="flex items-end gap-2 max-w-4xl mx-auto">
          <input 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
          />
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-3 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 bg-gray-100 dark:bg-gray-700 rounded-full transition-colors flex-shrink-0"
            disabled={status !== ConnectionState.CONNECTED}
          >
            <LucidePlus size={24} />
          </button>
          
          <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-2xl px-4 py-3 flex items-center focus-within:ring-2 focus-within:ring-blue-500 transition-shadow">
            <input 
              value={inputMsg}
              onChange={e => setInputMsg(e.target.value)}
              placeholder={status === ConnectionState.CONNECTED ? "Type a message..." : "Waiting for partner..."}
              className="w-full bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 resize-none"
              disabled={status !== ConnectionState.CONNECTED}
            />
          </div>

          <button 
            type="submit" 
            disabled={!inputMsg.trim() || status !== ConnectionState.CONNECTED}
            className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 transition-all active:scale-95 flex-shrink-0"
          >
            <LucideSend size={24} />
          </button>
        </form>
      </footer>
    </div>
  );
};

export default App;
